import React, { createContext, useContext, useMemo, useRef } from 'react';

/**
 * Whether leaving the current page right now would discard work.
 */
export type LeaveGuard = () => boolean;

type EditModeGuardValue = {
  /**
   * Register the current page's guard, or pass null to clear it.
   *
   * Stored in a ref, so registering never re-renders anything.
   */
  setLeaveGuard: (guard: LeaveGuard | null) => void;
  /** Asks the registered guard. False when no page has registered one. */
  shouldWarnBeforeLeaving: () => boolean;
};

/**
 * Lets a page tell the router-level navigation blocker that leaving would lose work.
 *
 * The blocker lives above the router, in ProtectedRoute, because that is where react-router's
 * data router is available. The state it needs to make the decision — is edit mode on, are
 * there unsaved changes — lives inside the page. This carries the answer across that gap,
 * so the blocker asks the page instead of guessing from localStorage flags that other code
 * paths also write.
 */
const EditModeGuardContext = createContext<EditModeGuardValue | null>(null);

/** Used where no provider is mounted, so a page can register a guard nobody reads. */
const NO_GUARD: EditModeGuardValue = {
  setLeaveGuard: () => {},
  shouldWarnBeforeLeaving: () => false,
};

export const EditModeGuardProvider = ({ children }: { children: React.ReactNode }) => {
  const guardRef = useRef<LeaveGuard | null>(null);

  const value = useMemo<EditModeGuardValue>(
    () => ({
      setLeaveGuard: (guard) => {
        guardRef.current = guard;
      },
      shouldWarnBeforeLeaving: () => (guardRef.current ? guardRef.current() : false),
    }),
    []
  );

  return <EditModeGuardContext.Provider value={value}>{children}</EditModeGuardContext.Provider>;
};

/**
 * The leave guard registry. Falls back to a no-op outside a provider, so a page that
 * registers a guard still renders wherever the provider is not mounted.
 */
export const useEditModeGuard = (): EditModeGuardValue =>
  useContext(EditModeGuardContext) ?? NO_GUARD;
